import { useIsMobile } from "@elit-hair/elit-ui";
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { PermissionModal } from "@/components/permissions-modal/permissions-modal";

type PermissionsContextType = {
    showModal: boolean;
    closeModal: () => void;
    openModal: () => void;
    permissions: Record<PermissionNames, PermissionState | null>;
    validatePermissions: (permissionName: PermissionNames) => boolean;
    requestPermission: (permissionName: PermissionNames) => void;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export enum PermissionNames {
    NOTIFICATIONS = "notifications",
    MICROPHONE = "microphone",
    SPEAKER = "speaker",
}

const toPermissionState = (permission: NotificationPermission): PermissionState => {
    if (permission === "default") {
        return "prompt";
    }
    return permission;
};

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isMobile = useIsMobile();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [permissions, setPermissions] = useState<Record<PermissionNames, PermissionState | null>>({
        [PermissionNames.NOTIFICATIONS]: null,
        [PermissionNames.MICROPHONE]: null,
        [PermissionNames.SPEAKER]: null,
    });

    useEffect(() => {
        navigator.permissions.query({ name: PermissionNames.NOTIFICATIONS }).then((permissionStatus) => {
            setPermissions((prev) => ({ ...prev, [PermissionNames.NOTIFICATIONS]: permissionStatus.state }));
            permissionStatus.onchange = () => {
                setPermissions((prev) => ({ ...prev, [PermissionNames.NOTIFICATIONS]: permissionStatus.state }));
            };
        });

        navigator.permissions.query({ name: PermissionNames.MICROPHONE as PermissionName }).then((permissionStatus) => {
            setPermissions((prev) => ({ ...prev, [PermissionNames.MICROPHONE]: permissionStatus.state }));
            permissionStatus.onchange = () => {
                setPermissions((prev) => ({ ...prev, [PermissionNames.MICROPHONE]: permissionStatus.state }));
            };
        });
    }, []);

    const requestMicPermission = () => {
        // Keep getUserMedia in the click turn; stop tracks after the prompt resolves.
        void navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
                stream.getTracks().forEach((track) => track.stop());
            })
            .catch((error: unknown) => {
                console.error("Error requesting microphone permission:", error);
            });
    };

    const requestNotificationPermission = () => {
        if (!("Notification" in window)) {
            return;
        }

        // Chrome only shows the permission UI when this runs in the same
        // synchronous turn as the user click — do not await anything before this.
        void Notification.requestPermission().then((permission) => {
            setPermissions((prev) => ({
                ...prev,
                [PermissionNames.NOTIFICATIONS]: toPermissionState(permission),
            }));
        });
    };

    const requestPermission = (permissionName: PermissionNames) => {
        if (permissionName === PermissionNames.NOTIFICATIONS) {
            requestNotificationPermission();
            return;
        }

        requestMicPermission();
    };

    const validatePermissions = useCallback(
        (permissionName: PermissionNames) => {
            if (isMobile) {
                return true;
            }

            if (permissions[permissionName] !== "granted") {
                setIsModalOpen(true);
                return false;
            }

            setIsModalOpen(false);
            return true;
        },
        [isMobile, permissions]
    );

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    return (
        <PermissionsContext.Provider
            value={{
                showModal: isModalOpen,
                closeModal,
                openModal,
                permissions,
                validatePermissions,
                requestPermission,
            }}
        >
            {children}
            <PermissionModal isOpen={isModalOpen} onClose={closeModal} />
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error("usePermissions must be used within a PermissionsProvider");
    }
    return context;
};

export const usePermissionsModalOpen = () => {
    const context = useContext(PermissionsContext);
    if (context === undefined) {
        throw new Error("usePermissionsModalOpen must be used within a PermissionsProvider");
    }
    return context.showModal;
};
